import type { BaseHTMLAttributes, ImgHTMLAttributes } from 'react'
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url'
import { LazyImage as ImageLegacy } from '@/scopes/common/components/ImageLegacy'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { space, tok } from '@sunwebgroup/ui-components'
import { Icon, Typography } from '@sunwebgroup/ui-components/uds'
import { useState } from 'react'
import { styled } from 'styled-components'

// TODO review Typography variants and colors with designers ACTIVATE_UDS_SEARCH_PRODUCT_CARD
// Changing this we are affecting several other scopes, but should be okay if we standardize styles in UDS
export interface ImageProps extends Pick<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'className' | 'loading' | 'alt'> {
  fallbackText?: string
  size?: 'sm' | 'default'
  sizes?: string
  src: string
  srcSet?: number[]
}

interface ImageFallbackProps extends BaseHTMLAttributes<HTMLDivElement> {
  fallbackText?: string
  size: ImageProps['size']
}

export const LazyImagePlaceholder = styled.img`
  filter: blur(12px);
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 500ms ease-out;
`

const StyledError = styled.div`
  align-items: center;
  background: ${tok('semantics_views_tinted_background_color')};
  color: ${tok('semantics_content_secondary_color')};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 0 ${space('primitives_space_16')};
  text-align: center;
  width: 100%;
`

const StyledText = styled(Typography)`
  margin-top: ${space('primitives_space_8')};
  max-width: 11rem;
`

const StyledImage = styled.img<{ $loaded: boolean }>`
  position: absolute; // to put it on top of the placeholder
  opacity: ${props => (props.$loaded ? 1 : 0)};
  transition: opacity 500ms ease-out;
`
const ImageWrapper = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
  overflow: hidden; // hide overlapping filter blur from sibilings
`

export const LazyImage = styled(Image)`
  height: 100%;
  object-fit: cover;
  width: 100%;
`

export const LazyImageFallback = styled(ImageFallback)``

function Image({ fallbackText, size = 'default', sizes, src, srcSet = [], ...attributes }: ImageProps) {
  const [imageError, setImageError] = useState(false)
  const [imageIsLoaded, setImageIsLoaded] = useState(false)
  const ACTIVATE_UDS_SEARCH_PRODUCT_CARD = useFeatureFlag('ACTIVATE_UDS_SEARCH_PRODUCT_CARD')

  if (!ACTIVATE_UDS_SEARCH_PRODUCT_CARD) {
    return <ImageLegacy fallbackText={fallbackText} size={size} sizes={sizes} src={src} srcSet={srcSet} {...attributes} />
  }

  if (imageError) {
    return (
      <ImageWrapper>
        <LazyImageFallback size={size} fallbackText={fallbackText} />
      </ImageWrapper>
    )
  }

  return (
    <ImageWrapper>
      <LazyImagePlaceholder
        {...attributes}
        alt={attributes.alt ?? ''}
        src={buildImgSrc(src, 20)}
        loading="eager"
        onErrorCapture={() => {
          setImageError(true)
        }}
      />
      <StyledImage
        {...attributes}
        $loaded={imageIsLoaded}
        alt={attributes.alt ?? ''}
        onErrorCapture={() => setImageError(true)}
        onLoadCapture={() => setImageIsLoaded(true)}
        sizes={sizes}
        src={srcSet.length > 0 ? buildImgSrc(src, srcSet[0]) : src}
        srcSet={srcSet.length > 0 ? buildImgSrcSet(src, srcSet) : undefined}
      />
    </ImageWrapper>
  )
}

Image.displayName = 'LazyImage'

function ImageFallback({ fallbackText, size, ...attributes }: ImageFallbackProps): JSX.Element {
  return (
    <StyledError {...attributes}>
      <Icon glyph="ImageSlashed" size={size === 'default' ? 32 : 16} />
      <StyledText $variant={size === 'default' ? 'body-l-bold-snug' : 'body-s-regular-snug'}>{fallbackText}</StyledText>
    </StyledError>
  )
}
